/**
 * Portfolio Listing - editorial grid + lightbox modal frontend.
 *
 * Build entry: src/js/modules/portfolio-listing/frontend.js
 * Output:      assets/js/modules/portfolio-listing-frontend.min.js
 *
 * Responsibilities:
 *   1. Entrance animation (UiCore protocol) - cards carry data-ui-animation /
 *      data-ui-duration / --ui-anim-delay and start with the
 *      `uicore-animate-hide` class; an IntersectionObserver adds `animated`
 *      (+ speed class) and the animation-name classes, then reveals exactly
 *      when the CSS animation starts.
 *   2. Card click -> lightbox modal. When modal is enabled the card carries
 *      `js-open-modal`; the payload rides on a sibling <template
 *      class="mypro-portfolio-modal-data"> matched via data-index, plus the
 *      widget-level data-mypro-portfolio-modal-options JSON. When modal is
 *      off and the card is a plain link, clicking navigates to the post.
 *
 * Loaded only when:
 *   1. the Portfolio Listing feature is enabled (module manager),
 *   2. Elementor renders the widget on the page (get_script_depends).
 *
 * Self-contained IIFE - no imports, no build-time shared chunks.
 */
(() => {
	

	// ------------------------------------------------------------------
	// Debug plumbing (MYPRO_DEBUG)
	//
	// Enable by any of:
	//   1. define('MYPRO_DEBUG', true) in wp-config.php  -> render() /
	//      content_template() add a data-mypro-portfolio-debug attribute to
	//      the widget markup, which this bundle detects and reacts to;
	//   2. window.__myproPortfolioDebug = true from the preview console;
	//   3. ?mypro_debug=1 in the page URL (frontend only).
	// ------------------------------------------------------------------

	var DEBUG = false;

	function isDebugUrl() {
		return (
			typeof window.location === 'object' &&
			window.location.search &&
			window.location.search.indexOf('mypro_debug=1') !== -1
		);
	}

	/** Re-evaluate the debug switch (cheap; called at every boot/init). */
	function refreshDebug() {
		if (window.__myproPortfolioDebug) {
			DEBUG = true;
			return true;
		}
		if (isDebugUrl()) {
			DEBUG = true;
			return true;
		}
		if (
			document.querySelector &&
			document.querySelector('[data-mypro-portfolio-debug]')
		) {
			DEBUG = true;
			return true;
		}
		return DEBUG;
	}

	function dbg() {
		if (!DEBUG) {
			return;
		}
		var args = Array.prototype.slice.call(arguments);
		args.unshift('[mypro-portfolio]');
		if (typeof window.console !== 'undefined' && window.console.debug) {
			window.console.debug.apply(window.console, args);
		} else if (typeof window.console !== 'undefined' && window.console.log) {
			window.console.log.apply(window.console, args);
		}
	}

	/** Stable node id so log lines can tell re-used vs fresh DOM nodes apart. */
	var nodeCounter = 0;
	function nodeId(node) {
		if (!node) {
			return '(none)';
		}
		if (!node.__myproPortfolioNodeId) {
			node.__myproPortfolioNodeId = ++nodeCounter;
		}
		return '#' + node.__myproPortfolioNodeId + ' ' + node.tagName;
	}

	refreshDebug();
	dbg('script boot', 'readyState=' + document.readyState);

	/** Respect the OS-level reduced-motion setting. */
	function prefersReducedMotion() {
		return (
			typeof window.matchMedia === 'function' &&
			window.matchMedia('(prefers-reduced-motion: reduce)').matches
		);
	}

	// One shared IntersectionObserver for every card on the page.
	var observer = null;

	function getObserver() {
		if (observer) {
			return observer;
		}
		observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) {
						dbg('intersect skip (not intersecting)', nodeId(entry.target));
						return;
					}
					var el = entry.target;

					dbg(
						'intersect reveal',
						nodeId(el),
						'anim=' + (el.getAttribute('data-ui-animation') || '(none)'),
						'delay=' + (getComputedStyle(el).animationDelay || '?')
					);

					var speed = el.getAttribute('data-ui-duration');
					if (speed === 'fast') {
						el.classList.add('animated', 'animated-fast');
					} else if (speed === 'slow') {
						el.classList.add('animated', 'animated-slow');
					} else {
						el.classList.add('animated');
					}

					var animName = el.getAttribute('data-ui-animation');
					if (animName) {
						el.classList.add.apply(el.classList, animName.split(' '));
					}

					var onStart = () => {
						dbg('animationstart reveal', nodeId(el));
						el.classList.remove('uicore-animate-hide');
						el.removeEventListener('animationstart', onStart);
					};
					el.addEventListener('animationstart', onStart, { once: true });

					requestAnimationFrame(() => {
						if (getComputedStyle(el).animationDelay === '0s') {
							dbg('rAF zero-delay reveal', nodeId(el));
							el.classList.remove('uicore-animate-hide');
						}
					});

					observer.unobserve(el);
				});
			},
			{ rootMargin: '10px', threshold: 0 }
		);
		return observer;
	}

	// ------------------------------------------------------------------
	// Modal / lightbox
	// ------------------------------------------------------------------

	/** Root-level modal nodes (one shared backdrop for the whole page). */
	var modalBackdrop = null;
	var modalPanel = null;
	var modalBody = null;
	var modalImage = null;

	/**
	 * Lazily build (and return) the shared modal DOM.
	 */
	function getModal() {
		if (modalBackdrop) {
			return modalBackdrop;
		}

		modalBackdrop = document.createElement('div');
		modalBackdrop.className = 'mypro-portfolio-modal__backdrop';
		modalBackdrop.setAttribute('role', 'dialog');
		modalBackdrop.setAttribute('aria-modal', 'true');
		modalBackdrop.setAttribute('aria-label', 'Portfolio');
		modalBackdrop.hidden = true;

		modalPanel = document.createElement('div');
		modalPanel.className = 'mypro-portfolio-modal__panel';

		var close = document.createElement('button');
		close.type = 'button';
		close.className = 'mypro-portfolio-modal__close';
		close.setAttribute('aria-label', 'Close');

		modalImage = document.createElement('div');
		modalImage.className = 'mypro-portfolio-modal__media';

		modalBody = document.createElement('div');
		modalBody.className = 'mypro-portfolio-modal__body';

		modalPanel.appendChild(close);
		modalPanel.appendChild(modalImage);
		modalPanel.appendChild(modalBody);
		modalBackdrop.appendChild(modalPanel);
		document.body.appendChild(modalBackdrop);

		// Close: button, backdrop click (not on the panel), Escape.
		close.addEventListener('click', closeModal);
		modalBackdrop.addEventListener('click', (e) => {
			if (e.target === modalBackdrop) {
				closeModal();
			}
		});
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && modalBackdrop && !modalBackdrop.hidden) {
				closeModal();
			}
		});

		return modalBackdrop;
	}

	/**
	 * Parse + cache the widget-level modal options.
	 */
	function readModalOptions(root) {
		var raw = root.getAttribute('data-mypro-portfolio-modal-options');
		if (!raw) {
			return {
				closeText: 'Close',
				linkText: 'View Project',
				width: 84,
				widthUnit: 'vw',
				showTitle: true,
				showMeta: true,
				showExcerpt: true,
				showLink: true,
			};
		}
		try {
			return JSON.parse(raw);
		} catch (e) {
			return {
				closeText: 'Close',
				linkText: 'View Project',
				width: 84,
				widthUnit: 'vw',
				showTitle: true,
				showMeta: true,
				showExcerpt: true,
				showLink: true,
			};
		}
	}

	/**
	 * Find the <template> payload for a card by its zero-based data-index.
	 */
	function payloadForCard(root, index) {
		var templates = root.querySelectorAll('template.mypro-portfolio-modal-data');
		for (var i = 0; i < templates.length; i++) {
			if (templates[i].getAttribute('data-index') === String(index)) {
				// A <template> element's children live in .content (a
				// DocumentFragment), not in its childNodes — so textContent is
				// always '' here. jQuery's parseHTML (used by Elementor's editor
				// re-render path) also moves the content into .content. Read it
				// from there, otherwise the payload silently becomes {} and the
				// modal opens blank (image src="", empty body).
				var node = templates[i];
				var raw = '';
				if (node.content && typeof node.content.textContent === 'string') {
					raw = node.content.textContent;
				} else {
					raw = node.textContent || node.innerHTML || '';
				}
				if (!raw) {
					return null;
				}
				try {
					return JSON.parse(raw);
				} catch (e) {
					return null;
				}
			}
		}
		return null;
	}

	/** Open the modal for a card. */
	function openModal(root, card) {
		var index = card.getAttribute('data-index');
		var payload = payloadForCard(root, index);
		if (!payload) {
			return; // No payload (e.g. a stored template missing templates).
		}
		var options = readModalOptions(root);

		var backdrop = getModal();
		var close = backdrop.querySelector('.mypro-portfolio-modal__close');
		close.textContent = options.closeText || 'Close';

		// Panel width from the widget setting.
		modalPanel.style.setProperty(
			'--mypro-portfolio-modal-w',
			(options.width || 84) + (options.widthUnit || 'vw')
		);

		// Media.
		while (modalImage.firstChild) {
			modalImage.removeChild(modalImage.firstChild);
		}
		if (payload.image) {
			var img = document.createElement('img');
			img.src = payload.image || '';
			img.alt = payload.alt || '';
			img.loading = 'lazy';
			modalImage.appendChild(img);
		}

		// Body.
		while (modalBody.firstChild) {
			modalBody.removeChild(modalBody.firstChild);
		}
		var category = payload.category || '';
		if (options.showMeta !== false && category) {
			var cat = document.createElement('div');
			cat.className = 'mypro-portfolio-modal__category';
			cat.textContent = category;
			modalBody.appendChild(cat);
		}

		if (options.showTitle !== false && payload.title) {
			var title = document.createElement('h3');
			title.className = 'mypro-portfolio-modal__title';
			title.textContent = payload.title;
			modalBody.appendChild(title);
		}

		if (options.showMeta !== false && payload.meta) {
			// The meta string is generated server-side and contains only
			// escaped text inside a single <span> - parse it with the inert
			// DOMParser (never innerHTML) so no script can execute.
			var meta = document.createElement('div');
			meta.className = 'mypro-portfolio-modal__meta';
			var metaText = String(payload.meta || '');
			if (metaText.indexOf('<') === -1) {
				meta.textContent = metaText;
			} else if (typeof DOMParser !== 'undefined') {
				var doc = new DOMParser().parseFromString(metaText, 'text/html');
				var metaNode = doc.body && doc.body.firstChild;
				if (metaNode) {
					meta.appendChild(doc.importNode(metaNode, true));
				} else {
					meta.textContent = metaText;
				}
			} else {
				meta.textContent = metaText;
			}
			modalBody.appendChild(meta);
		}

		if (options.showExcerpt !== false && payload.excerpt) {
			var excerpt = document.createElement('p');
			excerpt.className = 'mypro-portfolio-modal__excerpt';
			excerpt.textContent = payload.excerpt;
			modalBody.appendChild(excerpt);
		}

		if (options.showLink !== false && payload.link) {
			var link = document.createElement('a');
			link.className = 'mypro-portfolio-modal__link';
			link.href = payload.link;

			var linkLabel = document.createElement('span');
			linkLabel.textContent = options.linkText || 'View Project';
			link.appendChild(linkLabel);

			// Arrow icon (static, no user input).
			var iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
			iconSvg.setAttribute('viewBox', '0 0 16 16');
			iconSvg.setAttribute('fill', 'none');
			var iconPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
			iconPath.setAttribute('d', 'M1 8h13M9 3l5 5-5 5');
			iconPath.setAttribute('stroke', 'currentColor');
			iconPath.setAttribute('stroke-width', '1.6');
			iconPath.setAttribute('stroke-linecap', 'round');
			iconPath.setAttribute('stroke-linejoin', 'round');
			iconSvg.appendChild(iconPath);
			link.appendChild(iconSvg);

			modalBody.appendChild(link);
		}

		activeWidgetRoot = root;

		activeWidgetRoot = root;

		backdrop.hidden = false;
		// Force reflow so the transition plays from the closed state.
		void backdrop.offsetWidth;
		backdrop.classList.add('is-open');

		// Lock the page scroll while open.
		document.body.style.overflow = 'hidden';

		// Focus the close button for keyboard users.
		close.focus();
	}

	/** Close the modal. */
	function closeModal() {
		if (!modalBackdrop || modalBackdrop.hidden) {
			return;
		}
		modalBackdrop.classList.remove('is-open');
		document.body.style.overflow = '';
		var onEnd = () => {
			modalBackdrop.hidden = true;
			modalBackdrop.removeEventListener('transitionend', onEnd);
		};
		modalBackdrop.addEventListener('transitionend', onEnd, { once: true });
	}

	// ------------------------------------------------------------------
	// Wiring
	// ------------------------------------------------------------------

	/**
	 * Wire one widget root: entrance animation + click behavior.
	 *
	 * @param {Element} scope Widget wrapper (.elementor-widget in the editor,
	 *                        .mypro-portfolio-listing from the sweep).
	 */
	function initRoot(scope) {
		refreshDebug();

		// Elementor's element_ready hook hands us the widget wrapper
		// (.elementor-widget), which is REUSED across editor re-renders while
		// its innerHTML — including our .mypro-portfolio-listing node — is
		// replaced. Flagging the wrapper would make data-mypro-portfolio-ready
		// persist across re-renders and skip re-initialization of the fresh
		// content. Resolve the actual listing node instead: it is recreated on
		// every render, so the flag is always scoped to live content.
		var root = scope;
		if (
			root &&
			root.querySelector &&
			root.classList &&
			root.classList.contains('elementor-widget')
		) {
			var listing = root.querySelector('.mypro-portfolio-listing');
			if (listing) {
				root = listing;
			}
		}

		if (DEBUG) {
			var debugPayload = root && root.getAttribute
				? root.getAttribute('data-mypro-portfolio-debug')
				: null;
			if (debugPayload) {
				dbg('debug payload', nodeId(root), debugPayload);
			}
		}
		if (
			!root ||
			!root.querySelector ||
			root.getAttribute('data-mypro-portfolio-ready')
		) {
			dbg(
				'initRoot SKIP',
				nodeId(root),
				'ready=' + (root ? root.getAttribute('data-mypro-portfolio-ready') : '(no root)'),
				'isWidgetEl=' + !!(root && root.classList && root.classList.contains('elementor-widget'))
			);
			return;
		}
		root.setAttribute('data-mypro-portfolio-ready', '1');

		var cards = root.querySelectorAll('.portfolio-card');
		dbg(
			'initRoot run',
			nodeId(root),
			'cards=' + cards.length,
			'isWidgetEl=' + !!(root.classList && root.classList.contains('elementor-widget')),
			'modal=' + root.getAttribute('data-mypro-portfolio-modal')
		);
		var modalEnabled = root.getAttribute('data-mypro-portfolio-modal') !== 'no';

		// Entrance animation.
		if (prefersReducedMotion()) {
			dbg('reduced-motion: reveal all immediately');
			for (var r = 0; r < cards.length; r++) {
				cards[r].classList.remove('uicore-animate-hide');
			}
		} else {
			for (var j = 0; j < cards.length; j++) {
				if (cards[j].hasAttribute('data-ui-animation')) {
					dbg('observe card', nodeId(cards[j]), 'index=' + cards[j].getAttribute('data-index'));
					getObserver().observe(cards[j]);
				} else {
					dbg('card without animation: reveal', nodeId(cards[j]));
					cards[j].classList.remove('uicore-animate-hide');
				}
			}
		}

		// Click behavior.
		for (var i = 0; i < cards.length; i++) {
			if (cards[i].__myproPortfolioWired) {
				continue;
			}
			cards[i].__myproPortfolioWired = true;

			if (modalEnabled) {
				cards[i].addEventListener('click', ((card) => (e) => {
						if (e.defaultPrevented) {
							return;
						}
						e.preventDefault();
						openModal(root, card);
					})(cards[i]));
			} else if (cards[i].classList.contains('is-link')) {
				// Plain link: let the browser navigate (card is <article>,
				// so an anchor lives inside - nothing else to do; the class
				// keeps cursor:pointer).
			}
		}

		// Pagination: navigate to the next page (browser follows the link)
		// but first scroll to the widget so the new cards land in view
		// instead of dropping the user at the top of the page.
		var pagination = root.querySelector('.mypro-portfolio-pagination');
		if (pagination && !pagination.__myproPortfolioWired) {
			pagination.__myproPortfolioWired = true;
			pagination.addEventListener('click', (e) => {
				var link = e.target && e.target.closest
					? e.target.closest('a.page-numbers')
					: null;
				if (!link || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
					return;
				}
				var target = root;
				if (target && target.scrollIntoView) {
					try {
						target.scrollIntoView({ behavior: 'smooth', block: 'start' });
					} catch (err) {
						target.scrollIntoView(true);
					}
				}
			});
		}
	}

	/** Sweep the document for every widget root. */
	function initAll() {
		refreshDebug();
		var roots = document.querySelectorAll('.mypro-portfolio-listing');
		dbg('initAll sweep', 'roots=' + roots.length);
		for (var i = 0; i < roots.length; i++) {
			initRoot(roots[i]);
		}
	}

	/**
	 * Register the Elementor `element_ready` hook. Runs only after
	 * `elementorFrontend.init()` (the "elementor/frontend/init" window
	 * event), because the public page boots before this script and the editor
	 * boots the iframe instance later. Both the suffixed and bare widget type
	 * names are registered.
	 */
	function registerElementReadyHook() {
		if (
			!window.elementorFrontend ||
			!window.elementorFrontend.hooks ||
			window.elementorFrontend.hooks.__myproPortfolioListingReady
		) {
			return;
		}
		window.elementorFrontend.hooks.__myproPortfolioListingReady = true;

		var onReady = ($scope) => {
			dbg(
				'element_ready fired',
				nodeId($scope && $scope[0] ? $scope[0] : $scope),
				'readyAttr=' + ($scope && $scope[0] ? $scope[0].getAttribute('data-mypro-portfolio-ready') : '(no scope)')
			);
			initRoot($scope && $scope[0] ? $scope[0] : $scope);
		};
		window.elementorFrontend.hooks.addAction(
			'frontend/element_ready/mypro_portfolio_listing.default',
			onReady
		);
		window.elementorFrontend.hooks.addAction(
			'frontend/element_ready/mypro_portfolio_listing',
			onReady
		);
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => {
			dbg('DOMContentLoaded -> initAll');
			initAll();
		});
	} else {
		dbg('immediate initAll');
		initAll();
	}

	if (window.elementorFrontend && window.elementorFrontend.hooks) {
		registerElementReadyHook();
	}
	window.addEventListener('elementor/frontend/init', registerElementReadyHook);

	// Version-proof fallback: rAF-throttled sweep for widget nodes that appear
	// after this script ran (editor drag-in, control re-render, lazy panels).
	var sweepQueued = false;
	function queueSweep() {
		if (sweepQueued) {
			return;
		}
		sweepQueued = true;
		requestAnimationFrame(() => {
			sweepQueued = false;
			dbg('mutation sweep');
			initAll();
		});
	}
	if (typeof MutationObserver !== 'undefined') {
		new MutationObserver(() => {
			queueSweep();
		}).observe(document.documentElement, {
			childList: true,
			subtree: true,
		});
	}
})();
